import React, { useCallback, useEffect, useRef } from 'react';
import { ReactComponent as CloseIcon } from '../assets/images/close-icon.svg';
import { useIsMobile } from '../hooks/useIsMobile';

interface ModalProps {
  open: boolean;
  onClose?: () => void,
  children: React.ReactNode,
  /**
   * `CONTAINER` is the editorial refresh's panel — navy, light text, the same
   * radius and border as SurfaceCard. `DARK` is the older `bg-dark-grey` one
   * and is left alone: RepositoryBrowser is built on it.
   */
  theme?: "LIGHT" | "DARK" | "CONTAINER",
  showCloseIcon?: boolean,
  minWidth?: string,
  innerPadding?: boolean
}

const Modal = ({ open, onClose, children, minWidth, theme = 'LIGHT', showCloseIcon=true, innerPadding=true }: ModalProps) => {
  const modalContentRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  if (typeof onClose === "undefined") {
    onClose = () => {};
  }

  const handleClickOutsideEvent = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
        onClose && onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutsideEvent);
    document.addEventListener('touchstart', handleClickOutsideEvent);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideEvent);
      document.removeEventListener('touchstart', handleClickOutsideEvent);
    }
  }, [handleClickOutsideEvent]);

  if (!open) {
    return <></>
  }

  const isContainerTheme = theme === 'CONTAINER';
  const bgColor = theme === 'LIGHT'
    ? 'bg-white'
    : isContainerTheme ? 'bg-navy-800 border border-navy-500 shadow-[0_8px_32px_rgba(0,0,0,.5)]' : 'bg-dark-grey';
  const textColor = isContainerTheme ? 'text-navy-100' : 'text-dark';
  const radius = isContainerTheme ? 'rounded-container' : 'rounded-lg';
  const iconColor = theme === 'LIGHT' ? '' : isContainerTheme ? 'text-navy-300 hover:text-white' : 'text-white';

  return <>
    {/* The container screens blur what is behind a dialog so the dialog is the
        only thing left to read. The older themes are shared with the Agents
        screens and keep their plain dim. */}
    <div className={`fixed top-0 left-0 right-0 bottom-0 w-full h-full z-[9999] ${isContainerTheme ? 'bg-navy-950/60 backdrop-blur-md' : 'bg-black/60'}`}></div>
    <div 
      className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${bgColor} ${radius} z-[9999] ${textColor} animate-slide-in-center ${isMobile ? 'w-[calc(100vw-32px)]' : ''}`} 
      ref={modalContentRef} 
      style={{ minWidth: isMobile ? undefined : (minWidth ?? '25rem'), maxWidth: '90vw' }}
    >
      <div className={`relative ${innerPadding ? 'px-4 py-4 lg:px-8' : ''} max-h-[calc(100vh-80px)] overflow-y-auto`}>
        {showCloseIcon ? <CloseIcon className={`absolute right-3 top-3 cursor-pointer ${iconColor}`} width="10px" height="10px" onClick={onClose} /> : null}
        {children}
      </div>
    </div>
  </>
}

export default Modal;